import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  ClipboardCheck,
  Loader2,
  ListChecks,
} from 'lucide-react';
import {
  fetchStartingEvaluation,
  submitStartingEvaluation,
} from '../api/candidate';
import type {
  StartingEvaluation,
  StartingEvalQuestion,
  StartingEvalSection,
} from '../types';

type Answers = Record<string, string | string[]>;

/** Une question à choix unique / quiz (boutons radio stylés). */
function SingleChoice({
  q,
  value,
  onChange,
}: {
  q: StartingEvalQuestion;
  value: string | undefined;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {q.options?.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-3.5 py-2 rounded-lg border text-sm font-medium transition ${
              active
                ? 'bg-indigo-600 border-indigo-600 text-white'
                : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-300'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function MultiChoice({
  q,
  value,
  onChange,
}: {
  q: StartingEvalQuestion;
  value: string[] | undefined;
  onChange: (v: string[]) => void;
}) {
  const selected = value ?? [];
  const toggle = (v: string) =>
    onChange(
      selected.includes(v)
        ? selected.filter((x) => x !== v)
        : [...selected, v],
    );
  return (
    <div className="flex flex-wrap gap-2">
      {q.options?.map((opt) => {
        const active = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={`px-3.5 py-2 rounded-lg border text-sm font-medium transition ${
              active
                ? 'bg-indigo-600 border-indigo-600 text-white'
                : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-300'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function QuestionField({
  q,
  answers,
  setAnswer,
}: {
  q: StartingEvalQuestion;
  answers: Answers;
  setAnswer: (code: string, v: string | string[]) => void;
}) {
  const raw = answers[q.code];
  return (
    <div className="py-4 border-t border-slate-100 first:border-t-0 first:pt-0">
      <p className="text-sm font-medium text-slate-800 mb-2.5">
        {q.label}
        {!q.optional && <span className="text-indigo-500"> *</span>}
      </p>
      {q.type === 'multi' ? (
        <MultiChoice
          q={q}
          value={Array.isArray(raw) ? raw : []}
          onChange={(v) => setAnswer(q.code, v)}
        />
      ) : q.type === 'text' ? (
        <input
          type="text"
          value={typeof raw === 'string' ? raw : ''}
          onChange={(e) => setAnswer(q.code, e.target.value)}
          className="w-full px-3.5 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 text-sm"
          placeholder="Votre réponse…"
        />
      ) : (
        <SingleChoice
          q={q}
          value={typeof raw === 'string' ? raw : undefined}
          onChange={(v) => setAnswer(q.code, v)}
        />
      )}
      {q.hint && <p className="text-xs text-slate-400 mt-2">{q.hint}</p>}
    </div>
  );
}

/** Liste des questions obligatoires non répondues. */
function missingRequired(
  sections: StartingEvalSection[],
  answers: Answers,
): string[] {
  const missing: string[] = [];
  for (const s of sections) {
    for (const q of s.questions) {
      if (q.optional) continue;
      const v = answers[q.code];
      const empty =
        v == null ||
        (typeof v === 'string' && v.trim() === '') ||
        (Array.isArray(v) && v.length === 0);
      if (empty) missing.push(q.code);
    }
  }
  return missing;
}

function CompletedSummary({ data }: { data: StartingEvaluation }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm text-center">
      <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4 mx-auto">
        <CheckCircle2 size={26} />
      </div>
      <h2 className="text-slate-900 mb-1">Évaluation de départ réalisée</h2>
      {data.estimatedHours != null && <p className="text-indigo-700 mt-4"><strong>Estimation initiale : {data.estimatedHours} h de conduite</strong><br />À confirmer avec votre moniteur après l’évaluation pratique.</p>}
      <p className="text-sm text-slate-500">
        Merci ! Votre auto-école a bien reçu vos réponses. Elles serviront de
        base pour personnaliser votre formation.
      </p>
      {data.knowledgeMax != null && (
        <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-xl bg-indigo-50 border border-indigo-100">
          <ListChecks size={16} className="text-indigo-600" />
          <span className="text-sm text-slate-700">
            Connaissance du véhicule :{' '}
            <b className="text-indigo-700">
              {data.knowledgeScore}/{data.knowledgeMax}
            </b>
          </span>
        </div>
      )}
    </div>
  );
}

export default function EvaluationView() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['candidate', 'starting-evaluation'],
    queryFn: fetchStartingEvaluation,
    staleTime: 60 * 1000,
  });

  const [answers, setAnswers] = useState<Answers>({});
  const [showErrors, setShowErrors] = useState(false);
  const setAnswer = (code: string, v: string | string[]) =>
    setAnswers((prev) => ({ ...prev, [code]: v }));

  const mutation = useMutation({
    mutationFn: () => submitStartingEvaluation(answers),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['candidate', 'starting-evaluation'], fresh);
    },
  });

  const missing = useMemo(
    () => (data ? missingRequired(data.definition.sections, answers) : []),
    [data, answers],
  );

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex items-center text-slate-500 text-sm">
        <Loader2 className="animate-spin mr-2" size={16} /> Chargement…
      </div>
    );
  }

  if (isError || !data || data.status === 'not_requested') {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-slate-900">Évaluation de départ</h1>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-10 shadow-sm text-center">
          <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-4 mx-auto">
            <ClipboardCheck className="text-indigo-600" size={26} />
          </div>
          <p className="text-sm text-slate-500">
            Aucune évaluation de départ ne vous est demandée pour le moment.
          </p>
        </div>
      </div>
    );
  }

  if (data.status === 'completed') {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-slate-900">Évaluation de départ</h1>
        </div>
        <CompletedSummary data={data} />
      </div>
    );
  }

  // status === 'pending' → formulaire
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-slate-900">Évaluation de départ</h1>
        <p className="text-slate-600">
          Ce court bilan aide votre auto-école à personnaliser votre formation.
          Répondez le plus sincèrement possible — il n'y a pas d'enjeu de
          réussite.
        </p>
      </div>

      {data.definition.sections.map((section) => (
        <div
          key={section.code}
          className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-slate-900">{section.title}</h2>
            {section.scored && (
              <span className="text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2 py-0.5">
                {data.definition.knowledgeMax} questions
              </span>
            )}
          </div>
          {section.description && (
            <p className="text-sm text-slate-500 mb-3">{section.description}</p>
          )}
          <div>
            {section.questions.map((q) => (
              <QuestionField
                key={q.code}
                q={q}
                answers={answers}
                setAnswer={setAnswer}
              />
            ))}
          </div>
        </div>
      ))}

      {showErrors && missing.length > 0 && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          Merci de répondre à toutes les questions obligatoires (
          {missing.length} restante{missing.length > 1 ? 's' : ''}).
        </div>
      )}
      {mutation.isError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          Une erreur est survenue lors de l'envoi. Merci de réessayer.
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => {
            if (missing.length > 0) {
              setShowErrors(true);
              return;
            }
            mutation.mutate();
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors"
        >
          {mutation.isPending ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <CheckCircle2 size={16} />
          )}
          {mutation.isPending ? 'Envoi…' : 'Valider mon évaluation'}
        </button>
      </div>
    </div>
  );
}
